<script setup lang="ts">
/**
 * Renders a rich option entry with title, meta (e.g., manufacturer), and body (e.g., description).
 * Keeps presentation concerns encapsulated for reuse in option lists.
 */
interface Props {
  name?: string
  manufacturer?: string
  description?: string
  active?: boolean
  selected?: boolean
  isDisabled?: boolean
  date?: string
}

const props = withDefaults(defineProps<Props>(), {
  name: '',
  manufacturer: '',
  description: '',
  active: false,
  selected: false,
  isDisabled: false,
  date: '',
})
</script>

<template>
  <div
    class="rich-option"
    :class="{ 'rich-option--active': props.active, 'rich-option--selected': props.selected, 'rich-option--disabled': props.isDisabled }"
    :title="props.name"
  >
    <div class="rich-option__head">
      <div class="rich-option__headline">
        <span
          class="by-copy-large-semibold rich-option__title"
          :class="{ 'rich-option__title--three-line': props.date }"
        >
          <slot name="name">{{ props.name }}</slot>
        </span>
        <div class="by-copy-large-regular rich-option__meta">
          <div class="rich-option__manufacturer">
            <span>
              <slot name="meta">{{ props.manufacturer }}</slot>
            </span>
          </div>
          <div class="rich-option__date">
            {{ props.date }}
          </div>
        </div>
      </div>
    </div>
    <div class="rich-option__body">
      <span class="copy-large-regular rich-option__body-text">
        <slot>{{ props.description }}</slot>
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
.rich-option {
  color: var(--neutral-80);
  display: flex;
  flex-direction: column;
  gap: 0;
  width: 100%;
  cursor: pointer;

  &__head {
    padding: var(--Spacing-2, 8px) var(--Spacing-4, 24px);
  }

  &__headline {
    display: flex;
    justify-content: space-between;
    gap: var(--Spacing-2, 8px);

    .rich-option__title, .rich-option__manufacturer {
      width: 100%;
      overflow: hidden;
      display: -webkit-box;
      line-clamp: 2;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    .rich-option__title--three-line {
      line-clamp: 3;
      -webkit-line-clamp: 3;
    }

    .rich-option__manufacturer, .rich-option__date {
      color: var(--neutral-60);
      font-weight: 400;
      text-align: right;
    }

  }

  &__body {
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    line-clamp: 3;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    padding: var(--Spacing-2, 8px) var(--Spacing-4, 24px) 0 var(--Button-Radius, 24px);
    margin-bottom: var(--Spacing-2, 8px);
  }

  &__body-text {
    color: var(--neutral-60)!important;
  }

  &:not(:last-child) {
    border-bottom: 1px solid var(--neutral-20);
  }

  &--active {
    background-color: var(--neutral-20);
  }

  &--selected {
    background: var(--blue-0, #aedff8);
  }

  &--disabled:not(&--active, &--selected) {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &[data-headlessui-state="disabled"] {
    cursor: not-allowed;
    opacity: 0.5;
  }
}
</style>
